import { useEffect, useRef, useState } from "react";
import { recordPrewarm } from "@/lib/perfLog";
import { resolveTransitionPlayback } from "../transitionPlayback";

const WARM_BYTES = 256 * 1024;
const WARM_DELAY_MS = 800;
const MAX_WARM_TRANSITIONS = 2;

/**
 * Speculative transition work must never compete with the visible scene.
 * Once it can play, warm at most two distinct adjacent videos, one bounded
 * range at a time. No hidden video decoders or eager destination downloads.
 */
export function useScenePrewarm(structure, activeSceneId, buildEffectiveTransitions, enabled = false) {
  const cacheRef = useRef(new Map());
  const [, setVersion] = useState(0);

  useEffect(() => {
    const connection = navigator.connection;
    if (!enabled || !structure || !activeSceneId || connection?.saveData
      || /(^|-)2g$/.test(connection?.effectiveType || "")) return undefined;

    const seen = new Set();
    const transitions = buildEffectiveTransitions(structure.transitions, activeSceneId)
      .map(t => ({ ...t, warmUrl: resolveTransitionPlayback(t, !!t._reverse).url }))
      .filter(t => {
        const url = t.warmUrl;
        if (!(t.scrubbable_enabled || t.transition_type === "auto_video") || !url || seen.has(url)) return false;
        seen.add(url);
        return true;
      }).slice(0, MAX_WARM_TRANSITIONS);
    const controller = new AbortController();
    const cache = cacheRef.current;
    const created = [];

    const timer = setTimeout(async () => {
      for (const transition of transitions) {
        if (controller.signal.aborted || document.visibilityState === "hidden") break;
        const startedAt = performance.now();
        const entry = { video: null, ready: false, startedAt };
        cache.set(transition.id, entry);
        created.push(transition.id);
        try {
          const response = await fetch(transition.warmUrl, {
            headers: { Range: `bytes=0-${WARM_BYTES - 1}` },
            signal: controller.signal, credentials: "include", priority: "low",
          });
          const length = Number(response.headers.get("content-length"));
          // Do not accidentally download a whole cinematic if a server
          // ignores Range or provides an unbounded response.
          if (!response.ok || !length || length > WARM_BYTES) {
            await response.body?.cancel?.();
            continue;
          }
          await response.arrayBuffer();
          if (controller.signal.aborted) break;
          entry.ready = true;
          recordPrewarm(transition.id, "hit", performance.now() - startedAt, {
            url: transition.warmUrl, scene: activeSceneId, strategy: "bounded-range",
          });
          setVersion(n => n + 1);
        } catch (_) {
          if (controller.signal.aborted) break;
          recordPrewarm(transition.id, "miss", performance.now() - startedAt, {
            url: transition.warmUrl, scene: activeSceneId, reason: "fetch_error",
          });
        }
      }
    }, WARM_DELAY_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
      created.forEach(id => cache.delete(id));
    };
  }, [structure, activeSceneId, buildEffectiveTransitions, enabled]);

  return { cacheRef, isReady: id => !!cacheRef.current.get(id)?.ready };
}
